/**
 * A new pay run: what it pays, what it withholds, and how the net pay left the
 * bank (spec §18.6).
 *
 * NOTHING IS STORED UNTIL A PREVIEW IS CONFIRMED, and the preview is the
 * server's own calculation -- the same one the run stores -- so what is read
 * here is what is booked. No sum on this screen is computed twice.
 *
 * CHANGING AN ANSWER THROWS THE PREVIEW AWAY. A preview of other numbers is
 * worse than none, because it would be confirmed as though it described the pay
 * now on screen.
 *
 * NEVER BOOK PAY TWICE (§18.6). Net pay leaves through Mercury and the sync has
 * already made a draft for that debit, so a run CODES it: the uncoded debits of
 * exactly the net pay within a week of the pay date are offered, and the
 * operator picks one. A draft of our own is for a run prepared before payday;
 * the run's page exchanges it for the bank's when it arrives.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  api,
  type Account,
  type EmployeeSummary,
  type RunPreview,
  type RunRequestBody,
} from '../api.js';
import { employeeName, periodWord } from '../lib/labels.js';
import { formatCents, normaliseAmountInput, parseCents } from '../lib/money.js';
import { PayrollDeadlines } from '../components/PayrollDeadlines.js';

type Paid = { kind: 'debit'; headerId: string } | { kind: 'bank'; accountId: string };

export function NewPayRunPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeSummary[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [employeeId, setEmployeeId] = useState('');
  const [payDate, setPayDate] = useState('');
  const [regular, setRegular] = useState('');
  const [bonus, setBonus] = useState('');
  const [bonusMethod, setBonusMethod] = useState<'flat' | 'aggregate'>('flat');
  const [premium, setPremium] = useState('');

  const [preview, setPreview] = useState<RunPreview | null>(null);
  const [paid, setPaid] = useState<Paid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listEmployees(), api.listAccounts()])
      .then(([people, chart]) => {
        if (cancelled) return;
        setEmployees(people.employees);
        setAccounts(chart.accounts);
        // 0083: anyone can be paid, so only a business with one employee has an obvious choice.
        if (people.employees.length === 1) setEmployeeId(people.employees[0]!.id);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the employees.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Any change to what is paid leaves the preview describing something else. */
  function change(set: (value: string) => void) {
    return (value: string) => {
      setPreview(null);
      setPaid(null);
      setError(null);
      set(value);
    };
  }

  function amounts(): RunRequestBody | string {
    if (employeeId === '') return 'Choose who is being paid.';
    if (payDate === '') return 'Enter the pay date.';
    const regularCents = regular.trim() === '' ? undefined : parseCents(regular);
    if (regularCents === null) return 'Regular pay is not an amount.';
    const bonusCents = parseCents(bonus);
    if (bonusCents === null) return 'The bonus is not an amount.';
    const premiumCents = parseCents(premium);
    if (premiumCents === null) return 'The health insurance premiums are not an amount.';
    return {
      employeeId,
      payDate,
      ...(regularCents === undefined ? {} : { regularWagesCents: regularCents }),
      supplemental: bonusCents > 0 ? { wagesCents: bonusCents, method: bonusMethod } : null,
      healthPremiumCents: premiumCents,
    };
  }

  async function runPreview() {
    const body = amounts();
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.previewPayrollRun(body);
      setPreview(result.preview);
      setPaid(null);
    } catch (caught) {
      setPreview(null);
      setError(caught instanceof ApiError ? caught.message : 'Could not work out the pay.');
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    const body = amounts();
    if (typeof body === 'string' || preview === null) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createPayrollRun({
        ...body,
        ...(paid?.kind === 'debit' ? { bankDebitHeaderId: paid.headerId } : {}),
        ...(paid?.kind === 'bank' ? { bankAccountId: paid.accountId } : {}),
      });
      navigate(`/payroll/runs/${created.run.id}`, { state: { postRefusal: created.postRefusal } });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the pay run.');
      setBusy(false);
    }
  }

  const bankAccounts = accounts.filter(
    (account) => account.accountRole === 'bank' && account.type === 'asset' && account.isActive,
  );
  const net = preview?.result.netPayCents ?? 0;
  const ready = preview !== null && (net === 0 || paid !== null);

  return (
    <section className="payroll">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>New Pay Run</h1>

      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <div className="new-account" role="group" aria-label="What Is Being Paid">
        <div className="employee-fields">
          <div className="field-row">
            <label className="field">
              <span>Employee</span>
              <select value={employeeId} onChange={(event) => change(setEmployeeId)(event.target.value)}>
                <option value="">Choose…</option>
                {(employees ?? []).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employeeName(employee)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field-medium">
              <span>Pay Date</span>
              <input type="date" value={payDate} onChange={(event) => change(setPayDate)(event.target.value)} />
            </label>
          </div>
          <div className="field-row">
            <label className="field field-medium">
              <span>Regular Pay</span>
              <input
                inputMode="decimal"
                placeholder="the pay rate's amount"
                value={regular}
                onChange={(event) => change(setRegular)(event.target.value)}
                onBlur={() => setRegular(normaliseAmountInput(regular))}
              />
            </label>
            <label className="field field-medium">
              <span>Bonus</span>
              <input
                inputMode="decimal"
                value={bonus}
                onChange={(event) => change(setBonus)(event.target.value)}
                onBlur={() => setBonus(normaliseAmountInput(bonus))}
              />
            </label>
            <label className="field field-medium">
              <span>Health Insurance Premiums</span>
              <input
                inputMode="decimal"
                value={premium}
                onChange={(event) => change(setPremium)(event.target.value)}
                onBlur={() => setPremium(normaliseAmountInput(premium))}
              />
            </label>
          </div>
          {(parseCents(bonus) ?? 0) > 0 && (
            <fieldset className="choice choice-column">
              <legend>How the Bonus Is Withheld From</legend>
              <label>
                <input
                  type="radio"
                  name="bonus-method"
                  checked={bonusMethod === 'flat'}
                  onChange={() => {
                    setPreview(null);
                    setPaid(null);
                    setBonusMethod('flat');
                  }}
                />{' '}
                At the flat supplemental rate
              </label>
              <label>
                <input
                  type="radio"
                  name="bonus-method"
                  checked={bonusMethod === 'aggregate'}
                  onChange={() => {
                    setPreview(null);
                    setPaid(null);
                    setBonusMethod('aggregate');
                  }}
                />{' '}
                Added to regular pay, and withheld from together
              </label>
            </fieldset>
          )}
        </div>
        <div className="row">
          <button type="button" disabled={busy} onClick={() => void runPreview()}>
            Preview Pay Run
          </button>
        </div>
      </div>

      {preview && (
        <>
          <section className="settings-section" aria-labelledby="pay-run-preview">
            <h2 id="pay-run-preview">Preview</h2>
            <p className="muted">
              {preview.employee.name}, paid {preview.payDate}. Paid {formatCents(preview.payRate.amountPerPeriodCents)}{' '}
              each {periodWord(preview.payRate.frequency)} since {preview.payRate.effectiveOn}, on the W-4 taking effect{' '}
              {preview.w4.effectiveOn}.
            </p>

            <table className="lines" aria-label="What Is Paid">
              <tbody>
                <tr>
                  <td>Gross Pay</td>
                  <td className="amount">{formatCents(preview.result.grossPayCents)}</td>
                </tr>
                <tr>
                  <td>Federal Income Tax Withheld</td>
                  <td className="amount">{formatCents(preview.result.incomeTax.withheldCents)}</td>
                </tr>
                <tr>
                  <td>Social Security Withheld</td>
                  <td className="amount">{formatCents(preview.result.socialSecurity.employeeCents)}</td>
                </tr>
                <tr>
                  <td>Medicare Withheld</td>
                  <td className="amount">{formatCents(preview.result.medicare.employeeCents)}</td>
                </tr>
                {preview.result.medicare.additionalEmployeeCents > 0 && (
                  <tr>
                    <td>Additional Medicare Withheld</td>
                    <td className="amount">{formatCents(preview.result.medicare.additionalEmployeeCents)}</td>
                  </tr>
                )}
                <tr>
                  <td>
                    <strong>Net Pay</strong>
                  </td>
                  <td className="amount">
                    <strong>{formatCents(preview.result.netPayCents)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="lines" aria-label="What the Business Owes">
              <tbody>
                <tr>
                  <td>Social Security</td>
                  <td className="amount">{formatCents(preview.result.socialSecurity.employerCents)}</td>
                </tr>
                <tr>
                  <td>Medicare</td>
                  <td className="amount">{formatCents(preview.result.medicare.employerCents)}</td>
                </tr>
                <tr>
                  <td>FUTA</td>
                  <td className="amount">{formatCents(preview.result.futa.employerCents)}</td>
                </tr>
                <tr>
                  <td>Texas Unemployment</td>
                  <td className="amount">{formatCents(preview.result.texasUnemployment.employerCents)}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Payroll Taxes</strong>
                  </td>
                  <td className="amount">
                    <strong>{formatCents(preview.employerTaxesCents)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="lines" aria-label="The Entry">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="amount">Debit</th>
                  <th className="amount">Credit</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((line, index) => (
                  <tr key={`${line.accountId}-${index}`}>
                    <td>
                      {line.accountCode} {line.accountName}
                      {/* 0083: a share of the wages or employer taxes, by the employee's allocation. */}
                      {(line.fundCode || line.projectName) && (
                        <span className="muted"> · {[line.fundCode, line.projectName].filter(Boolean).join(' · ')}</span>
                      )}
                    </td>
                    <td className="amount">{line.debitCents > 0 ? formatCents(line.debitCents) : ''}</td>
                    <td className="amount">{line.creditCents > 0 ? formatCents(line.creditCents) : ''}</td>
                  </tr>
                ))}
                {net > 0 && (
                  <tr>
                    <td className="muted">The bank account the net pay leaves</td>
                    <td className="amount" />
                    <td className="amount">{formatCents(net)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          {/*
            Shown BEFORE the run is created, deliberately. The deposit date is
            the one consequence of a pay run that falls outside the books
            entirely, and a date seen only afterwards is one the operator has
            already had to guess at.
          */}
          <PayrollDeadlines payDate={preview.payDate} deadlines={preview.deadlines} />

          {net > 0 && (
            <section className="settings-section" aria-labelledby="pay-run-paid">
              <h2 id="pay-run-paid">How Net Pay Was Paid</h2>
              <fieldset className="choice choice-column">
                <legend>The bank side of this pay</legend>
                {preview.bankDebits.map((debit) => (
                  <label key={debit.headerId}>
                    <input
                      type="radio"
                      name="pay-run-paid"
                      checked={paid?.kind === 'debit' && paid.headerId === debit.headerId}
                      onChange={() => setPaid({ kind: 'debit', headerId: debit.headerId })}
                    />{' '}
                    Mercury debit on {debit.entryDate} · {debit.counterparty ?? debit.bankMemo ?? 'no description'} ·{' '}
                    {formatCents(Math.abs(debit.amountCents))} · {debit.accountCode} {debit.accountName}
                  </label>
                ))}
                {bankAccounts.map((account) => (
                  <label key={account.id}>
                    <input
                      type="radio"
                      name="pay-run-paid"
                      checked={paid?.kind === 'bank' && paid.accountId === account.id}
                      onChange={() => setPaid({ kind: 'bank', accountId: account.id })}
                    />{' '}
                    Not paid yet — make a draft from {account.code} {account.name}
                  </label>
                ))}
              </fieldset>
              {preview.bankDebits.length === 0 && (
                <p className="muted">
                  No Mercury debit of {formatCents(net)} has arrived within a week of {preview.payDate}. A draft made now
                  is exchanged for the bank's transaction on the run's page once it does.
                </p>
              )}
            </section>
          )}

          <div className="row">
            <button type="button" disabled={busy || !ready} onClick={() => void create()}>
              Create Pay Run
            </button>
            <button type="button" className="link" onClick={() => navigate('/payroll')}>
              Cancel
            </button>
          </div>
        </>
      )}
    </section>
  );
}
